import { getEntityColor } from '@/components/entity-colors';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import createDataSource from '@/hooks/useDataSource';
import { mergeGridConfig } from '@/lib/grid-config';
import { createConfigMutation } from '@/queries/createConfigMutation';
import { useCreateStore } from '@/stores/create-entity-store';

import { createFileRoute, Link } from '@tanstack/react-router'
import DataGrid, { Column, type DataGridTypes } from 'devextreme-react/data-grid';
import { EllipsisVerticalIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_app/settings/users/')({
  component: RouteComponent,
})

function RouteComponent() {
  const module_id = 'settings';
  const entity_type_id = 'users';
  const { show: setVisibleCreate } = useCreateStore();
  const createConfig = createConfigMutation();
  const color = getEntityColor(entity_type_id);

  const dataSource = createDataSource('/users/grid');

  const heightOffset = 75; // Adjust this value based on your layout
  const widthOffset = 340; // Adjust this value based on your layout
  const [gridHeight, setGridHeight] = useState<number>(window.innerHeight - heightOffset);
  const [gridWidth, setGridWidth] = useState<number>(window.innerWidth - widthOffset);

  // Use default grid configuration with custom overrides for users grid
  const gridConfig = mergeGridConfig({
    groupPanel: { visible: false }, // Disable group panel for users    
  });

  useEffect(() => {
    const handleResize = () => {
      setGridHeight(window.innerHeight - heightOffset);
      setGridWidth(window.innerWidth - widthOffset);
    };

    window.addEventListener('resize', handleResize);


    // Set initial dimensions
    setGridHeight(Math.min(window.innerHeight - heightOffset, 1000));
    setGridWidth(window.innerWidth - widthOffset);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [heightOffset, widthOffset]);


  const cellRender = (data: DataGridTypes.ColumnCellTemplateData) => (<Link className=' text-blue-600 hover:underline' to='/settings/users/$id' params={{ id: data.data.id }}>
    {data.value}
  </Link>);

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
        style={{
          borderBottomColor: color?.primaryColor
        }}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/$module_id' params={{ module_id }} search={{
                  sort: [],
                  groupKeys: [],
                  rowGroupCols: [],
                  valueCols: []
                }}>
                  Settings
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/$module_id/$entity_type_id/segments/$segment_id' params={{ module_id, entity_type_id, segment_id: 'all' }} search={{
                  sortModel: [],
                  groupKeys: [],
                  rowGroupCols: [],
                  valueCols: []
                }}>
                  Users
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                All Users
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 ml-auto mr-5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to='/$module_id/$entity_type_id/imports' params={{ module_id, entity_type_id }} >
                  Imports
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-500">Delete</DropdownMenuItem>

            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={async () => {
              const result = await createConfig.mutateAsync('users');
              setVisibleCreate('settings', 'users', result.entity, result.view);
            }}
          >
            Create User
          </Button>
        </div>
      </header>

      <div className="h-full w-full " style={{ backgroundColor: color?.backgroundColor }}>
        {/* {showFilter && <QueryEditor entityTypeId={entity_type_id} filter={currentSegment} applyQuery={setAppliedFilter} />} */}

        <div style={{ width: "calc(100%-40px)", height: "98%", backgroundColor: color?.backgroundColor }} className="m-2">
          <div className='bg-white border border-gray-300 shadow-lg p-2 rounded-lg'>
            <DataGrid
              width={gridWidth}
              height={gridHeight}
              dataSource={dataSource}
              {...gridConfig}
            >
              <Column dataField="active" dataType="boolean" caption="Active" width={100} />
              <Column dataField="firstName" caption="First Name" width={150} cellRender={cellRender} />
              <Column dataField="lastName" caption="Last Name" width={150} cellRender={cellRender} />
              <Column dataField="emailAddress" caption="Email Address" width={250} />
              <Column dataField="userName" caption="User Name" width={150} />
              <Column dataField="enableMFA" caption="Enable MFA" dataType="boolean" width={120} />
              <Column dataField="isAdmin" caption="Is Admin" dataType="boolean" width={100} />
              <Column dataField="userGroup.name" caption="User Group" width={150} />
            </DataGrid>
          </div>

        </div>
      </div>
    </>
  );
}
